import React, { useState, useEffect, useRef } from "react";
import { Send, Loader2, Package, BarChart3, CheckCircle, AlertTriangle, LogOut, Sun, Moon } from "lucide-react";
import { API_CONFIG } from '../config/api';
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import AgentSidebar from "../components/AgentSidebar";
import { useChatHistory } from "../hooks/useChatHistory";

// Debug logging
console.log("=== INVENTORY AGENT DEBUG ===");
console.log("API_CONFIG:", API_CONFIG);
console.log("INVENTORY_API:", API_CONFIG.INVENTORY_API);
console.log("Type:", typeof API_CONFIG.INVENTORY_API);

const InventoryAgent = ({ onNavigate }) => {
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState("retrieve"); // "retrieve" or "modify"
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Prompt limit configuration (2 for testing, change to 10 for production)
  const MAX_PROMPTS_PER_CHAT = 10;

  // Chat history integration
  const [currentSessionId, setCurrentSessionId] = useState(`session-${Date.now()}`);
  const { chatSessions, saveChatSession, loadChatSession, deleteChatSession } = useChatHistory(
    user?.email || user?.username || 'anonymous',
    'inventory'
  );

  // Calculate number of user prompts in current chat
  const userPromptCount = messages.filter(msg => msg.sender === 'user').length;
  const isPromptLimitReached = userPromptCount >= MAX_PROMPTS_PER_CHAT;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Rename chat handler
  const handleRenameChat = async (sessionId, newTitle) => {
    try {
      const chatData = await loadChatSession(sessionId);
      if (!chatData) throw new Error('Failed to load chat data');
      if (!chatData.messages || !Array.isArray(chatData.messages)) {
        throw new Error('Invalid chat data');
      }
      await saveChatSession(sessionId, chatData.messages, newTitle);
    } catch (error) {
      console.error('Failed to rename chat:', error);
      alert(`Failed to rename chat: ${error.message}`);
    }
  };

  // Delete chat handler
  const handleDeleteChat = async (sessionId) => {
    try {
      await deleteChatSession(sessionId);
      if (sessionId === currentSessionId) {
        setMessages([]);
        setCurrentSessionId(`session-${Date.now()}`);
      }
    } catch (error) {
      console.error('Failed to delete chat:', error);
      alert('Failed to delete chat. Please try again.');
    }
  };

  // Share chat as PDF handler
  const handleShareChat = async (chat) => {
    try {
      console.log('📄 Exporting chat to PDF:', chat.sessionId);
      const { jsPDF } = await import('jspdf');
      const chatData = await loadChatSession(chat.sessionId);
      if (!chatData || !chatData.messages) {
        console.error('❌ Failed to load chat data for export');
        alert('Failed to load chat data');
        return;
      }

      console.log('✅ Chat data loaded, generating PDF...');
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      const checkNewPage = (requiredSpace) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 102, 0);
      doc.text(chatData.title, margin, yPosition);
      yPosition += 8;

      // Metadata
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(128, 128, 128);
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, yPosition);
      yPosition += 5;
      doc.text(`Messages: ${chatData.messages.length}`, margin, yPosition);
      yPosition += 8;

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 8;

      // Messages
      chatData.messages.forEach((msg, idx) => {
        const sender = msg.sender === 'user' ? 'You' : 'Inventory Agent';
        const senderColor = msg.sender === 'user' ? [0, 102, 204] : [255, 102, 0];

        checkNewPage(6);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...senderColor);
        doc.text(sender, margin, yPosition);
        yPosition += 6;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        const messageLines = doc.splitTextToSize(msg.text, maxWidth);

        messageLines.forEach(line => {
          checkNewPage(5);
          doc.text(line, margin, yPosition);
          yPosition += 5;
        });

        yPosition += 2;

        if (idx < chatData.messages.length - 1) {
          checkNewPage(8);
          doc.setDrawColor(220, 220, 220);
          doc.line(margin, yPosition, pageWidth - margin, yPosition);
          yPosition += 8;
        }
      });

      const fileName = `${chatData.title.replace(/[^a-z0-9]/gi, '_')}_chat.pdf`;
      doc.save(fileName);
      console.log('✅ PDF exported successfully:', fileName);
    } catch (error) {
      console.error('❌ Failed to export chat:', error);
      alert('Failed to export chat. Please try again.');
    }
  };

  // Load a specific chat session
  const handleLoadChat = async (sessionId) => {
    try {
      const chatData = await loadChatSession(sessionId);
      if (chatData && chatData.messages && Array.isArray(chatData.messages)) {
        setMessages(chatData.messages);
        setCurrentSessionId(sessionId);
      }
    } catch (error) {
      console.error('Failed to load chat:', error);
    }
  };

  const pushMessage = (msg) => setMessages((prev) => [...prev, msg]);

  const updateMessageText = (id, newText, isThinking = false, inventoryData = null, metrics = null) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === id ? { ...m, text: newText, isThinking, inventoryData, metrics } : m
      )
    );
  };

  const extractInventoryData = (text) => {
    try {
      console.log("=== EXTRACTING INVENTORY DATA ===");
      console.log("Text:", text);

      // Try to parse JSON response first
      try {
        const jsonData = JSON.parse(text);
        if (jsonData.items || jsonData.inventory || jsonData.products) {
          return jsonData.items || jsonData.inventory || jsonData.products;
        }
      } catch (e) {
        console.log("Not JSON, trying text parsing");
      }

      // Look for employee details pattern
      const employeeMatch = text.match(/Employee\s+(CRT\d+)\s+details:\s*\n-\s*Name:\s*([^\n]+)\s*\n-\s*Active employee:\s*([^\n]+)\s*\n-\s*Assets allocated:\s*([\s\S]*?)(?:\n\n|$)/i);

      if (employeeMatch) {
        const [, employeeId, name, activeStatus, assetsText] = employeeMatch;

        // Parse assets
        const assets = [];
        const assetMatches = assetsText.matchAll(/•\s*([^(]+)\s*\(([^,]+),\s*serial ID:\s*([^,]+),\s*assigned on\s*([^)]+)\)/g);

        for (const assetMatch of assetMatches) {
          const [, assetType, quantity, serialId, assignedDate] = assetMatch;
          assets.push({
            type: assetType.trim(),
            quantity: quantity.trim(),
            serialId: serialId.trim(),
            assignedDate: assignedDate.trim()
          });
        }

        return [{
          employeeId: employeeId,
          name: name.trim(),
          active: activeStatus.trim().toLowerCase() === 'yes',
          assets: assets,
          type: 'employee'
        }];
      }

      // Look for structured inventory data in text (fallback)
      const lines = text.split('\n');
      const items = [];

      for (const line of lines) {
        // Match patterns like "Product: Widget A, Stock: 150, Status: In Stock"
        const match = line.match(/(?:Product|Item):\s*([^,]+),?\s*(?:Stock|Quantity):\s*(\d+),?\s*(?:Status):\s*([^,\n]+)/i);
        if (match) {
          items.push({
            name: match[1].trim(),
            stock: parseInt(match[2]),
            status: match[3].trim(),
            type: 'product'
          });
        }
      }

      return items.length > 0 ? items : null;
    } catch (err) {
      console.error("Error extracting inventory data:", err);
      return null;
    }
  };

  // Determine which agent to use based on dropdown selection
  const determineAgentEndpoint = () => {
    if (selectedAgent === "modify") {
      console.log("🔧 Using Inventory Write Agent");
      return API_CONFIG.INVENTORY_WRITE_API;
    } else {
      console.log("📖 Using Inventory Read Agent");
      return API_CONFIG.INVENTORY_READ_API;
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim()) return;

    // Check prompt limit
    if (isPromptLimitReached) {
      alert(`You've reached the maximum of ${MAX_PROMPTS_PER_CHAT} prompts per chat. Please start a new chat to continue.`);
      return;
    }

    const text = inputValue;
    setInputValue("");

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = '40px';
    }

    const userId = `u-${Date.now()}`;
    const userMessage = { id: userId, text, sender: "user" };
    pushMessage(userMessage);

    const thinkingId = `b-${Date.now()}`;
    pushMessage({
      id: thinkingId,
      text: "Processing your request...",
      sender: "bot",
      isThinking: true,
    });

    setIsLoading(true);

    try {
      // Determine which API endpoint to use based on dropdown selection
      const apiUrl = determineAgentEndpoint();

      console.log("=== SENDING REQUEST TO AGENTCORE RUNTIME ===");
      console.log("Query:", text);
      console.log("URL:", apiUrl);
      console.log("Session ID:", currentSessionId);

      if (!apiUrl || apiUrl === 'undefined') {
        throw new Error(`API endpoint is undefined. Check config.`);
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Session-Id": currentSessionId,
        },
        body: JSON.stringify({
          prompt: text,
        }),
      });

      console.log("=== RESPONSE STATUS ===", response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("=== RAW RESPONSE DATA ===", data);

      // Parse the response - handle both direct agent response and API Gateway format
      let final = "";
      let responseMetrics = null;

      // Check if this is a direct agent response (has role and content)
      if (data.role === 'assistant' && data.content) {
        console.log("=== DIRECT AGENT RESPONSE ===");
        
        // Extract text from content
        if (Array.isArray(data.content) && data.content[0] && data.content[0].text) {
          let textContent = data.content[0].text;
          console.log("Raw text content:", textContent);
          
          // Check if text is a stringified Python dict (starts with {'role')
          if (typeof textContent === 'string' && textContent.trim().startsWith("{'role'")) {
            console.log("Detected stringified Python dict, parsing...");
            
            // Try to extract the actual text from the nested structure
            // Pattern: {'role': 'assistant', 'content': [{'text': 'ACTUAL_TEXT'}]}
            const nestedTextMatch = textContent.match(/'text':\s*["']([^"']*(?:\\.[^"']*)*)["']\s*}/);
            if (nestedTextMatch) {
              final = nestedTextMatch[1]
                .replace(/\\n/g, '\n')
                .replace(/\\'/g, "'")
                .replace(/\\"/g, '"')
                .replace(/\\\\/g, '\\');
              console.log("✅ Extracted nested text:", final);
            } else {
              // Fallback: try to find text between quotes after 'text':
              const fallbackMatch = textContent.match(/'text':\s*["'](.+?)["']\s*}/s);
              if (fallbackMatch) {
                final = fallbackMatch[1]
                  .replace(/\\n/g, '\n')
                  .replace(/\\'/g, "'")
                  .replace(/\\"/g, '"');
                console.log("✅ Extracted via fallback:", final);
              } else {
                // Last resort: use the raw text
                final = textContent;
                console.log("⚠️ Using raw text");
              }
            }
          } else {
            // Direct text content (not nested)
            final = textContent;
            console.log("✅ Direct text content");
          }
        }
        
        // Extract metrics if available
        if (data.metrics) {
          responseMetrics = data.metrics;
          console.log("=== EXTRACTED METRICS ===", responseMetrics);
        }
      }
      // Legacy API Gateway format
      else if (data.success && data.response) {
        console.log("=== API GATEWAY FORMAT ===");
        let response_text = data.response;

        // Extract metrics if available
        if (data.metrics) {
          responseMetrics = data.metrics;
          console.log("=== EXTRACTED METRICS ===", responseMetrics);
        }

        // Try to extract text from the Python dict format
        try {
          console.log("=== PARSING RESPONSE ===");
          console.log("Raw response_text:", response_text);

          // Method 1: Try regex to extract text from Python dict format
          const textMatch = response_text.match(/'text':\s*'([^']+(?:\\.[^']*)*)'}/);
          if (textMatch) {
            final = textMatch[1].replace(/\\n/g, '\n').replace(/\\'/g, "'");
            console.log("✅ Extracted via regex:", final);
          } else {
            // Method 2: Try to convert Python dict to JSON and parse
            try {
              // Replace single quotes with double quotes for JSON parsing
              let jsonString = response_text.replace(/'/g, '"');
              const parsed = JSON.parse(jsonString);

              if (parsed.role === 'assistant' && parsed.content && parsed.content[0] && parsed.content[0].text) {
                final = parsed.content[0].text;
                console.log("✅ Extracted via JSON parsing:", final);
              } else {
                final = response_text;
                console.log("⚠️ JSON parsed but no expected structure, using raw");
              }
            } catch (jsonError) {
              console.log("⚠️ JSON parsing failed:", jsonError);
              final = response_text;
            }
          }
        } catch (parse_error) {
          console.log("Response parsing failed, using raw response:", parse_error);
          final = response_text;
        }
      } else {
        final = data.error || "No response received from agent.";
      }

      console.log("=== FINAL TEXT ===", final);

      // Clean up the text
      if (typeof final === 'string') {
        final = final.replace(/\\n/g, "\n");
        final = final.replace(/\\"/g, '"');
        final = final.replace(/```json\n?|\n?```/g, '').trim();

        // Remove token usage from display text (it will show in footer)
        final = final.replace(/\s*Input:\s*[\d,]+\s*tokens\s*\n*Output:\s*[\d,]+\s*tokens\s*\n*Total:\s*[\d,]+\s*tokens\s*\n*[\d.]+ms\s*/gi, '');
        final = final.trim();
      }

      // Extract inventory data if present
      const inventoryData = extractInventoryData(final);
      console.log("=== EXTRACTED INVENTORY DATA ===", inventoryData);

      // Build the final bot message object
      const finalBotMessage = {
        id: thinkingId,
        text: final,
        sender: 'bot',
        isThinking: false,
        inventoryData,
        metrics: responseMetrics
      };

      // Update the message in UI
      updateMessageText(thinkingId, final, false, inventoryData, responseMetrics);

      // Auto-save chat history after bot responds
      try {
        // Generate a meaningful title from the user message
        const chatTitle = text.substring(0, 60) + (text.length > 60 ? '...' : '');

        // Build the complete messages array manually (since state updates are async)
        // Start with existing messages, add user message and bot response
        const completeMessages = [...messages, userMessage, finalBotMessage];

        console.log('=== SAVING CHAT HISTORY ===');
        console.log('Session ID:', currentSessionId);
        console.log('Messages count:', completeMessages.length);
        console.log('User message:', userMessage);
        console.log('Bot message:', finalBotMessage);
        console.log('Title:', chatTitle);

        await saveChatSession(currentSessionId, completeMessages, chatTitle);

        console.log('✅ Chat history saved with title:', chatTitle);
      } catch (saveError) {
        console.error('❌ Failed to save chat history:', saveError);
        console.error('Error details:', saveError.message);
      }
    } catch (err) {
      console.error("=== ERROR ===", err);
      let errorMessage = `⚠️ Error connecting to inventory agent: ${err.message}`;

      if (err.message.includes('401')) {
        errorMessage += "\n\nThis might be an authentication issue. The AgentCore Runtime may require proper authentication tokens.";
      } else if (err.message.includes('403')) {
        errorMessage += "\n\nAccess forbidden. Please check IAM permissions for the AgentCore Runtime.";
      } else if (err.message.includes('404')) {
        errorMessage += "\n\nRuntime not found. Please verify the runtime ARN and URL construction.";
      }

      updateMessageText(thinkingId, errorMessage, false);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('in stock') || statusLower.includes('available')) {
      return 'text-green-600 bg-green-100';
    } else if (statusLower.includes('low') || statusLower.includes('warning')) {
      return 'text-yellow-600 bg-yellow-100';
    } else if (statusLower.includes('out') || statusLower.includes('empty')) {
      return 'text-red-600 bg-red-100';
    }
    return 'text-gray-600 bg-gray-100';
  };

  const handleNewChat = () => {
    setMessages([]);
    setInputValue("");
    setCurrentSessionId(`session-${Date.now()}`);
    if (textareaRef.current) {
      textareaRef.current.style.height = '40px';
    }
  };

  return (
    <div className="flex w-full h-full overflow-hidden">
      {/* SIDEBAR */}
      <AgentSidebar
        currentAgent="inventory"
        onNavigate={onNavigate}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onNewChat={handleNewChat}
        chatHistory={chatSessions}
        onLoadChat={handleLoadChat}
        onRenameChat={handleRenameChat}
        onDeleteChat={handleDeleteChat}
        onShareChat={handleShareChat}
      />

      {/* MAIN CONTENT */}
      <div className="flex-1 flex flex-col bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 overflow-hidden transition-all duration-500 ease-in-out">
        {/* Theme Toggle - Top Right */}
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={(e) => toggleTheme(e)}
            className="theme-toggle-glow p-2 rounded-lg hover:bg-gray-200/50 dark:hover:bg-gray-700/50 transition-all duration-200 hover:scale-110 active:scale-95 hover:rotate-12"
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            <div className="relative">
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-yellow-500 transition-all duration-500 rotate-0" key="sun" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600 transition-all duration-500 rotate-0" key="moon" />
              )}
            </div>
          </button>
        </div>

        {/* Wrapper for centering when empty */}
        <div className={`flex-1 flex flex-col ${messages.length === 0 ? 'justify-center' : ''} overflow-hidden transition-opacity duration-500`}>
          {/* CHAT AREA */}
          <div className={`${messages.length === 0 ? '' : 'flex-1 overflow-y-auto'} p-3 sm:p-4 md:p-6`}>
            {messages.length === 0 ? (
              // BEAUTIFUL EMPTY STATE
              <div className="text-center max-w-xl mx-auto opacity-90 px-4">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <img
                    src="/CI-logo.png"
                    alt="CI Logo"
                    className="h-12 w-auto sm:h-14 md:h-16"
                  />
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900 dark:text-white">
                    Inventory Agent
                  </h2>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base md:text-lg mb-4 sm:mb-6">
                  Query inventory, add stock items, or update prices. Serial IDs are auto-generated when adding new items.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 text-left mb-8">
                  <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-lg shadow-sm border border-orange-200 dark:border-orange-700">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm sm:text-base">Query Examples:</h3>
                    <ul className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 space-y-1">
                      <li>• "Show me all laptops in stock"</li>
                      <li>• "What assets does CRT007 have?"</li>
                      <li>• "Total value of unassigned stock"</li>
                    </ul>
                  </div>
                  <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-lg shadow-sm border border-orange-200 dark:border-orange-700">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm sm:text-base">Modification Examples:</h3>
                    <ul className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 space-y-1">
                      <li>• "Add 5 laptops at ₹50,000 each"</li>
                      <li>• "Assign laptop LAP001 to CRT007"</li>
                      <li>• "Remove mouse from CRT007"</li>
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <div className="max-w-4xl mx-auto w-full space-y-4 sm:space-y-6">
                {messages.map((msg) => {
                  const isUser = msg.sender === "user";

                  return (
                    <div
                      key={msg.id}
                      className={`flex items-start gap-2 sm:gap-4 ${isUser ? "justify-end" : "justify-start"
                        }`}
                    >
                      {/* Avatar */}
                      {!isUser && (
                        <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-11 md:h-11 rounded-full bg-orange-600 flex items-center justify-center shadow-md flex-shrink-0">
                          <Package className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                        </div>
                      )}

                      {/* MESSAGE CONTENT */}
                      <div
                        className={`text-sm sm:text-[15px] leading-relaxed font-['system-ui',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif]
                      ${isUser
                            ? "inline-block bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 px-4 py-2 rounded-full"
                            : "max-w-[85%] sm:max-w-xl md:max-w-2xl text-gray-800 dark:text-gray-200"
                          }`}
                      >
                        {msg.isThinking ? (
                          <div className="flex items-center gap-3">
                            <Loader2 className="w-5 h-5 animate-spin text-orange-600" />
                            <span className="text-gray-700 dark:text-gray-300 font-medium">Processing inventory request...</span>
                          </div>
                        ) : (
                          <>
                            <div className={`whitespace-pre-wrap ${isUser ? 'text-gray-900 dark:text-gray-100' : 'text-gray-800 dark:text-gray-200'}`}>
                              {/* Render text with markdown-style bold formatting */}
                              {msg.text.split(/(\*\*[^*]+\*\*)/).map((part, index) => {
                                if (part.startsWith('**') && part.endsWith('**')) {
                                  return <strong key={index} className="text-lg font-bold">{part.slice(2, -2)}</strong>;
                                }
                                return part;
                              })}
                            </div>

                            {/* Inventory Data Display */}
                            {msg.inventoryData && msg.inventoryData.length > 0 && (
                              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                                <div className="flex items-center gap-2 mb-4">
                                  <BarChart3 className="w-5 h-5 text-orange-600" />
                                  <h3 className="font-semibold text-gray-900 dark:text-white text-base">
                                    {msg.inventoryData[0].type === 'employee' ? 'Employee Details' : 'Inventory Status'}
                                  </h3>
                                </div>
                                <div className="space-y-2.5">
                                  {msg.inventoryData.map((item, idx) => (
                                    <div key={idx}>
                                      {item.type === 'employee' ? (
                                        // Employee Details Display
                                        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                                          <div className="flex items-center gap-3 mb-3">
                                            <div className="w-9 h-9 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                                              <Package className="w-4 h-4" />
                                            </div>
                                            <div>
                                              <p className="font-semibold text-gray-900 text-[15px]">
                                                {item.name} ({item.employeeId})
                                              </p>
                                              <p className="text-sm text-gray-600">
                                                Status: {item.active ? 'Active Employee' : 'Inactive'}
                                              </p>
                                            </div>
                                            <div className="ml-auto">
                                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${item.active ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100'
                                                }`}>
                                                {item.active ? 'Active' : 'Inactive'}
                                              </span>
                                            </div>
                                          </div>

                                          {/* Assets */}
                                          {item.assets && item.assets.length > 0 && (
                                            <div className="mt-3 pt-3 border-t border-gray-200">
                                              <h4 className="font-medium text-gray-900 text-sm mb-2">Allocated Assets:</h4>
                                              <div className="space-y-2">
                                                {item.assets.map((asset, assetIdx) => (
                                                  <div key={assetIdx} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200">
                                                    <div className="flex items-center gap-2">
                                                      <div className="w-6 h-6 rounded bg-orange-100 flex items-center justify-center">
                                                        <Package className="w-3 h-3 text-orange-600" />
                                                      </div>
                                                      <div>
                                                        <p className="font-medium text-gray-900 text-sm">{asset.type}</p>
                                                        <p className="text-xs text-gray-600">Serial: {asset.serialId}</p>
                                                      </div>
                                                    </div>
                                                    <div className="text-right">
                                                      <p className="text-sm font-medium text-gray-900">{asset.quantity}</p>
                                                      <p className="text-xs text-gray-600">{asset.assignedDate}</p>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        // Product Inventory Display (existing)
                                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-all group cursor-pointer">
                                          <div className="flex items-center gap-3 flex-1">
                                            <div className="w-9 h-9 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                                              <Package className="w-4 h-4" />
                                            </div>
                                            <div className="flex-1">
                                              <p className="font-semibold text-gray-900 group-hover:text-orange-600 transition text-[15px]">
                                                {item.name}
                                              </p>
                                              <p className="text-sm text-gray-600">
                                                Stock: {item.stock} units
                                              </p>
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-3">
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                                              {item.status}
                                            </span>
                                            {item.status.toLowerCase().includes('in stock') ? (
                                              <CheckCircle className="w-5 h-5 text-green-600" />
                                            ) : (
                                              <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Metrics Footer */}
                            {msg.metrics && !isUser && (
                              <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                                <div className="text-xs text-gray-600 dark:text-gray-400 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                                  <div className="flex items-center gap-x-3">
                                    <span className="whitespace-nowrap">Input: {msg.metrics.inputTokens?.toLocaleString() || 0} tokens</span>
                                    <span className="whitespace-nowrap">Output: {msg.metrics.outputTokens?.toLocaleString() || 0} tokens</span>
                                  </div>
                                  <div className="flex items-center gap-x-3">
                                    <span className="whitespace-nowrap">Total: {msg.metrics.totalTokens?.toLocaleString() || 0} tokens</span>
                                    <span className="whitespace-nowrap">Elapsed Time: {msg.metrics.executionTimeMs ? `${msg.metrics.executionTimeMs}ms` : msg.metrics.latencyMs ? `${msg.metrics.latencyMs}ms` : 'N/A'}</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* USER AVATAR */}
                      {isUser && (
                        <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-11 md:h-11 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center shadow-lg flex-shrink-0">
                          <span className="text-white font-bold text-sm sm:text-base md:text-lg">U</span>
                        </div>
                      )}
                    </div>
                  );
                })}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* FLOATING INPUT BAR - Centered when empty, bottom when chat active */}
          <div className={`w-full flex flex-col items-center px-2 sm:px-0 transition-all duration-300 ${messages.length === 0 ? 'pb-[25vh]' : 'py-3 sm:py-4 md:py-6'}`}>
            {/* Prompt limit warning */}
            {isPromptLimitReached && (
              <div className="w-full sm:w-[90%] max-w-2xl mb-2 bg-orange-50 border border-orange-300 rounded-lg px-4 py-2 text-center">
                <p className="text-sm text-orange-800">
                  <span className="font-semibold">Prompt limit reached!</span> You've used {userPromptCount}/{MAX_PROMPTS_PER_CHAT} prompts.
                  <button
                    onClick={handleNewChat}
                    className="ml-2 text-orange-600 hover:text-orange-700 underline font-semibold"
                  >
                    Start a new chat
                  </button>
                </p>
              </div>
            )}

            {/* Prompt counter (show when not at limit) */}
            {!isPromptLimitReached && userPromptCount > 0 && (
              <div className="w-full sm:w-[90%] max-w-2xl mb-2 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {userPromptCount}/{MAX_PROMPTS_PER_CHAT} prompts used
                </p>
              </div>
            )}

            <div className="w-full sm:w-[90%] max-w-2xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-200 dark:border-gray-700 rounded-2xl px-3 sm:px-4 py-2 sm:py-2.5 shadow-lg flex flex-wrap sm:flex-nowrap items-center gap-2">
              {/* Input */}
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                disabled={isPromptLimitReached}
                placeholder="Ask about inventory, add stock, or update items..."
                rows={1}
                style={{
                  maxHeight: '288px',
                  minHeight: '40px',
                  resize: 'none',
                  overflow: 'auto',
                  paddingTop: '8px',
                  paddingBottom: '8px'
                }}
                onInput={(e) => {
                  const lineHeight = 24;
                  const maxLines = 12;
                  const padding = 16;
                  const maxHeight = (lineHeight * maxLines) + padding;

                  e.target.style.height = 'auto';
                  const newHeight = Math.min(e.target.scrollHeight, maxHeight);
                  e.target.style.height = newHeight + 'px';
                }}
                className={`flex-1 bg-transparent outline-none text-gray-800 dark:text-gray-200 text-sm sm:text-base ml-2 leading-6 placeholder:text-gray-400 dark:placeholder:text-gray-500 ${isPromptLimitReached ? 'opacity-50 cursor-not-allowed' : ''}`}
              />

              {/* Agent Selector Dropdown - Small and on the right */}
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs focus:outline-none focus:ring-1 focus:ring-orange-500 cursor-pointer flex-shrink-0"
                disabled={isPromptLimitReached}
              >
                <option value="retrieve">Retrieve</option>
                <option value="modify">Add / Modify / Delete</option>
              </select>

              {/* Send Button */}
              <button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading || isPromptLimitReached}
                className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full transition flex-shrink-0
              ${!inputValue.trim() || isLoading || isPromptLimitReached
                    ? "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    : "bg-orange-600 hover:bg-orange-700 text-white shadow-md"
                  }`}
              >
                <Send size={16} className="sm:w-[18px] sm:h-[18px]" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryAgent;